import { useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'

/**
 * The shared primitives.
 *
 * These are React components rather than CSS classes built with `@apply`:
 * with Tailwind, the component boundary is the reuse mechanism, and a class
 * like `.sp-btn` only re-invents one without the type checking.
 */

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'

export type ButtonVariant = 'primary' | 'light' | 'danger' | 'ghost'

/**
 * `md` is the form button: big enough to be the obvious next step at the end
 * of a form. `sm` is for page actions and toolbars, where a row of md buttons
 * would outweigh the data they act on.
 */
export type ButtonSize = 'sm' | 'md'

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  // Hover colours are restated for the link case: the base stylesheet colours
  // every <a> primary, which would otherwise win on a Link styled as a button.
  primary: 'bg-primary text-white hover:bg-primary-active hover:text-white',
  light: 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-700',
  danger: 'bg-danger text-white hover:brightness-95 hover:text-white',
  ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-900',
}

const BUTTON_SIZE: Record<ButtonSize, string> = {
  md: 'px-5 py-3 text-base',
  sm: 'h-9 px-3.5 text-sm',
}

/** The button look, for a Link or anything else that must look like one. */
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string {
  return cn(
    'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-60',
    FOCUS_RING,
    BUTTON_VARIANT[variant],
    BUTTON_SIZE[size],
    className,
  )
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return <button className={buttonClass(variant, size, className)} {...rest} />
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('rounded-lg bg-card p-7 shadow-card', className)}>{children}</div>
}

export function Field({
  id,
  label,
  error,
  type,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; error?: string }) {
  // Every password field gets a reveal toggle, rather than each page deciding.
  // Typing a password you cannot read is how "Tidak sama dengan kata sandi
  // baru" happens twice in a row, and the usual fix — pasting the same value
  // into both boxes — is worse than showing it.
  const isPassword = type === 'password'
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="mb-5 flex flex-col gap-1.5">
      <label className="font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>

      <div className="relative">
        <input
          id={id}
          // Switching the type is what reveals it. Keeping type="password" and
          // restyling the text would leave the browser still treating it as
          // secret, and the characters still dots.
          type={isPassword && revealed ? 'text' : type}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            'w-full rounded-md border px-4 py-3 text-base transition-colors focus:outline-none',
            // Room for the button, so a long password does not run underneath it.
            isPassword && 'pr-12',
            error
              ? 'border-danger bg-danger-light'
              : 'border-transparent bg-gray-100 focus:border-primary focus:bg-card',
          )}
          {...rest}
        />

        {isPassword && (
          <button
            type="button"
            // Focusable, not skipped: someone working by keyboard has the same
            // reason to check what they typed as anyone else.
            onClick={() => setRevealed((value) => !value)}
            aria-pressed={revealed}
            aria-label={revealed ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
            title={revealed ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
            className={cn(
              'absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-md',
              'text-gray-500 transition-colors hover:text-gray-700',
              FOCUS_RING,
            )}
          >
            <EyeIcon off={revealed} />
          </button>
        )}
      </div>

      {/* Inline, never a toast: a banner saying "invalid" while the offending
          field sits off-screen makes the user hunt for what went wrong. */}
      {error && (
        <span className="text-sm text-danger" id={`${id}-error`}>
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * An open eye while the password is hidden, a struck-through one while it is
 * showing.
 *
 * The glyph names the action, not the state: an open eye means "reveal this",
 * which is what the button will do when pressed. Drawing the current state
 * instead reverses the meaning and gets people clicking the wrong way round.
 */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {off ? (
        <>
          <path d="M10.6 10.6a3 3 0 0 0 4.24 4.24" />
          <path d="M9.9 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17.9 17.9 0 0 1-3.1 4.1M6.3 6.7A17.6 17.6 0 0 0 2 12s3.5 7 10 7a9.4 9.4 0 0 0 3.8-.8" />
          <path d="M3 3l18 18" />
        </>
      ) : (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
        </>
      )}
    </svg>
  )
}

/**
 * A checkbox with its label as one click target.
 *
 * The label wraps the input rather than pointing at it with htmlFor: a
 * checkbox is a ~16px target, and on a touch screen a label that is not part
 * of the target is the difference between ticking the box and missing it.
 */
export function Checkbox({
  id,
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { id: string; label: ReactNode }) {
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer select-none items-center gap-2.5 text-gray-700',
        className,
      )}
      htmlFor={id}
    >
      <input
        id={id}
        type="checkbox"
        className={cn(
          'size-4 cursor-pointer rounded-sm border border-gray-300 accent-primary transition-colors',
          FOCUS_RING,
        )}
        {...rest}
      />
      {label}
    </label>
  )
}

/**
 * The compact input look, for selects, dates and text in toolbars, settings
 * and dense document forms. `Field` stays the roomy control for short forms
 * such as login. Disabled is styled because a whole document goes read-only
 * at once (a disabled fieldset), and it should look it.
 */
export function controlClass(error?: string | boolean, className?: string): string {
  return cn(
    'h-9 w-full min-w-0 rounded-md border px-2.5 text-gray-900 transition-colors focus:outline-none',
    error ? 'border-danger bg-danger-light' : 'border-gray-300 bg-card focus:border-primary',
    'disabled:cursor-not-allowed disabled:border-line disabled:bg-gray-100 disabled:text-gray-700',
    className,
  )
}

export function Alert({
  variant = 'danger',
  className,
  children,
}: {
  variant?: 'danger' | 'info'
  /** `mb-0` inside a panel or a gapped stack, where the default bottom margin would double the spacing. */
  className?: string
  children: ReactNode
}) {
  // cn() only joins, it does not resolve conflicts, and Tailwind emits mb-5
  // after mb-0 -- so a margin passed in must replace the default, not join it.
  const ownMargin = /(^|\s)m[by]?-/.test(className ?? '')
  return (
    <div
      role="alert"
      className={cn(
        !ownMargin && 'mb-5',
        'rounded-md px-4 py-3',
        variant === 'danger'
          ? 'bg-danger-light text-danger'
          : 'bg-primary-light text-primary-active',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'size-10 animate-spin rounded-full border-[3px] border-gray-300 border-t-primary',
        className,
      )}
    />
  )
}
